import React, { useState, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { FaShieldAlt, FaMobileAlt, FaQrcode, FaCheck, FaCopy, FaChevronRight } from 'react-icons/fa';

/* ── 6 hane OTP ── */
const OtpInput = ({ value, onChange, onComplete }) => {
  const r0=useRef(),r1=useRef(),r2=useRef(),r3=useRef(),r4=useRef(),r5=useRef();
  const refs=[r0,r1,r2,r3,r4,r5];
  const digits = value.split('').concat(Array(6).fill('')).slice(0, 6);

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      onChange(digits.map((d,j) => j===i?'':d).join(''));
      if (i > 0) refs[i-1].current?.focus(); return;
    }
    if (!/^\d$/.test(e.key)) return;
    const next = digits.map((d,j) => j===i?e.key:d).join('').slice(0,6);
    onChange(next);
    if (i < 5) refs[i+1].current?.focus();
    if (next.length === 6) onComplete?.(next);
  };
  const handlePaste = (e) => {
    const p = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (p.length===6) { onChange(p); onComplete?.(p); refs[5].current?.focus(); }
    e.preventDefault();
  };

  return (
    <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
      {digits.map((d,i) => (
        <input key={i} ref={refs[i]} type="text" inputMode="numeric"
          maxLength={1} value={d} onChange={()=>{}}
          onKeyDown={e=>handleKey(i,e)} onPaste={handlePaste}
          onFocus={e=>e.target.select()}
          style={{
            width:48, height:56, textAlign:'center', borderRadius:14,
            background:d?'rgba(242,113,39,.1)':'rgba(255,255,255,.04)',
            border:`2px solid ${d?'rgba(242,113,39,.5)':'rgba(255,255,255,.08)'}`,
            color:'#f0eeff', fontSize:'1.4rem', fontWeight:700,
            fontFamily:"'DM Mono',monospace", outline:'none',
            transition:'all .15s', caretColor:'transparent',
            boxShadow:d?'0 0 18px rgba(242,113,39,.2)':'none',
          }}
        />
      ))}
    </div>
  );
};

const STEPS = [
  { icon: <FaMobileAlt/>, label: 'Hazırlık' },
  { icon: <FaQrcode/>, label: 'QR Kod' },
  { icon: <FaShieldAlt/>, label: 'Doğrula' },
];

export default function Setup2FA() {
  const [step,    setStep]    = useState(0); // 0=intro,1=qr,2=verify,3=done
  const [qrUrl,   setQrUrl]   = useState('');
  const [secret,  setSecret]  = useState('');
  const [otp,     setOtp]     = useState('');
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');
  const [copied,  setCopied]  = useState(false);

  const startSetup = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axiosInstance.post('/auth/2fa/setup');
      setQrUrl(r.data.qrUrl); setSecret(r.data.secret); setStep(1);
    } catch (e) { setErr(e.response?.data?.message || 'Kurulum başlatılamadı.'); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (code) => {
    const c = (code || otp).replace(/\s/g,'');
    if (c.length !== 6) { setErr('6 haneli kodu eksiksiz girin.'); return; }
    setBusy(true); setErr('');
    try {
      await axiosInstance.post('/auth/2fa/verify', { token: c });
      const u = JSON.parse(localStorage.getItem('adminUser') || '{}');
      u.twoFactorEnabled = true;
      localStorage.setItem('adminUser', JSON.stringify(u));
      setStep(3);
    } catch (e) { setErr(e.response?.data?.message || 'Kod hatalı.'); setOtp(''); }
    finally { setBusy(false); }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      minHeight:'100vh', background:'#08090b',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:"'Outfit',sans-serif", padding:20, position:'relative', overflow:'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=DM+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:.5}50%{opacity:1}}
        @keyframes scaleIn{from{opacity:0;transform:scale(.85)}to{opacity:1;transform:scale(1)}}
        .step-enter{animation:fadeUp .35s ease forwards;}
        .done-enter{animation:scaleIn .4s ease forwards;}
      `}</style>

      {/* Background */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <div style={{ position:'absolute', width:500, height:500, borderRadius:'50%', background:'radial-gradient(circle,rgba(242,113,39,.06),transparent 70%)', top:'50%', left:'50%', transform:'translate(-50%,-50%)' }} />
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,.012) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.012) 1px,transparent 1px)', backgroundSize:'60px 60px' }} />
      </div>

      <div style={{ width:'100%', maxWidth:440, position:'relative', zIndex:1 }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ width:56, height:56, borderRadius:18, margin:'0 auto 14px', background:'linear-gradient(135deg,#f27127,#a83300)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 40px rgba(242,113,39,.25), inset 0 1px 0 rgba(255,255,255,.15)' }}>
            <FaShieldAlt style={{ color:'#fff', fontSize:'1.2rem' }} />
          </div>
          <h1 style={{ color:'#f0eeff', fontWeight:800, fontSize:'1.2rem', letterSpacing:'-.3px' }}>İki Faktörlü Kimlik Doğrulama</h1>
          <p style={{ color:'rgba(240,238,255,.3)', fontSize:'.72rem', marginTop:5 }}>Hesabınızı güvence altına alın</p>
        </div>

        {/* Step indicators — sadece step 0-2'de göster */}
        {step < 3 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0, marginBottom:28 }}>
            {STEPS.map((s,i) => (
              <React.Fragment key={i}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5 }}>
                  <div style={{
                    width:34, height:34, borderRadius:10,
                    background: i < step ? 'rgba(16,185,129,.15)' : i === step ? 'rgba(242,113,39,.15)' : 'rgba(255,255,255,.04)',
                    border:`1px solid ${i < step ? 'rgba(16,185,129,.4)' : i === step ? 'rgba(242,113,39,.4)' : 'rgba(255,255,255,.07)'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color: i < step ? '#10b981' : i === step ? '#f27127' : 'rgba(255,255,255,.2)',
                    fontSize:'.75rem', transition:'all .3s',
                  }}>
                    {i < step ? <FaCheck/> : s.icon}
                  </div>
                  <span style={{ fontSize:'.58rem', fontWeight:600, color: i===step ? '#f27127' : 'rgba(255,255,255,.2)', textTransform:'uppercase', letterSpacing:'1px' }}>{s.label}</span>
                </div>
                {i < STEPS.length-1 && (
                  <div style={{ flex:1, height:1, background: i < step ? 'rgba(16,185,129,.3)' : 'rgba(255,255,255,.07)', margin:'0 8px', marginBottom:18, transition:'all .3s' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Card */}
        <div style={{ background:'rgba(255,255,255,.03)', borderRadius:24, border:'1px solid rgba(255,255,255,.07)', boxShadow:'0 32px 80px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.06)', overflow:'hidden' }}>
          <div style={{ height:2, background:'linear-gradient(90deg,transparent,rgba(242,113,39,.6),transparent)' }} />

          <div style={{ padding:'28px' }}>

            {/* STEP 0 — Intro */}
            {step === 0 && (
              <div className="step-enter">
                <div style={{ marginBottom:22 }}>
                  {[
                    { icon:'📱', title:'Authenticator Uygulaması', desc:'Google Authenticator veya Authy uygulamasını telefonunuza indirin.' },
                    { icon:'🔐', title:'QR Kodu Tarayın', desc:'Bir sonraki adımda gösterilecek QR kodu uygulamayla tarayın.' },
                    { icon:'✅', title:'Kodunuzu Girin', desc:'Uygulama tarafından üretilen 6 haneli kodu girerek doğrulayın.' },
                  ].map((item,i) => (
                    <div key={i} style={{ display:'flex', gap:14, padding:'12px', borderRadius:14, background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.05)', marginBottom:8 }}>
                      <div style={{ width:36, height:36, borderRadius:10, background:'rgba(242,113,39,.08)', border:'1px solid rgba(242,113,39,.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', flexShrink:0 }}>{item.icon}</div>
                      <div>
                        <div style={{ color:'#f0eeff', fontWeight:600, fontSize:'.82rem', marginBottom:3 }}>{item.title}</div>
                        <div style={{ color:'rgba(240,238,255,.4)', fontSize:'.72rem', lineHeight:1.5 }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {err && <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.2)', color:'#fca5a5', fontSize:'.78rem' }}>{err}</div>}
                <button onClick={startSetup} style={{ width:'100%', padding:'13px', borderRadius:14, border:'none', cursor:busy?'not-allowed':'pointer', background:busy?'rgba(242,113,39,.3)':'linear-gradient(135deg,#f27127,#c44b0a)', color:'#fff', fontSize:'.88rem', fontWeight:700, fontFamily:"'Outfit',sans-serif", boxShadow:busy?'none':'0 4px 24px rgba(242,113,39,.3)', opacity:busy?.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {busy ? <div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite' }} /> : <><span>Kurulumu Başlat</span><FaChevronRight style={{ fontSize:'.65rem' }} /></>}
                </button>
              </div>
            )}

            {/* STEP 1 — QR */}
            {step === 1 && (
              <div className="step-enter">
                <p style={{ color:'rgba(240,238,255,.45)', fontSize:'.77rem', marginBottom:20, textAlign:'center', lineHeight:1.6 }}>
                  Authenticator uygulamanızı açın ve aşağıdaki QR kodu tarayın
                </p>
                {qrUrl && (
                  <div style={{ display:'flex', justifyContent:'center', marginBottom:20 }}>
                    <div style={{ padding:16, background:'#fff', borderRadius:16, boxShadow:'0 8px 40px rgba(0,0,0,.4)' }}>
                      <img src={qrUrl} alt="QR Code" style={{ width:180, height:180, display:'block' }} />
                    </div>
                  </div>
                )}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:'.6rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'1.5px', color:'rgba(240,238,255,.25)', marginBottom:7 }}>Manuel Giriş Kodu</div>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <code style={{ flex:1, padding:'10px 13px', borderRadius:11, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.07)', color:'rgba(242,113,39,.8)', fontSize:'.72rem', fontFamily:"'DM Mono',monospace", letterSpacing:'2px', wordBreak:'break-all' }}>
                      {secret}
                    </code>
                    <button onClick={copySecret} style={{ padding:'10px 14px', borderRadius:11, border:'1px solid rgba(255,255,255,.07)', background: copied?'rgba(16,185,129,.1)':'rgba(255,255,255,.04)', color:copied?'#10b981':'rgba(255,255,255,.4)', cursor:'pointer', fontSize:'.8rem', display:'flex', alignItems:'center', gap:6, fontFamily:"'Outfit',sans-serif", transition:'all .2s', flexShrink:0, fontWeight:600 }}>
                      {copied?<><FaCheck/><span style={{fontSize:'.7rem'}}>Kopyalandı</span></>:<><FaCopy/><span style={{fontSize:'.7rem'}}>Kopyala</span></>}
                    </button>
                  </div>
                </div>
                <button onClick={()=>setStep(2)} style={{ width:'100%', padding:'13px', borderRadius:14, border:'none', cursor:'pointer', background:'linear-gradient(135deg,#f27127,#c44b0a)', color:'#fff', fontSize:'.88rem', fontWeight:700, fontFamily:"'Outfit',sans-serif", boxShadow:'0 4px 24px rgba(242,113,39,.3)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  <span>Kodu Taradım, Devam Et</span><FaChevronRight style={{ fontSize:'.65rem' }} />
                </button>
              </div>
            )}

            {/* STEP 2 — Verify */}
            {step === 2 && (
              <div className="step-enter">
                <div style={{ textAlign:'center', marginBottom:24 }}>
                  <p style={{ color:'rgba(240,238,255,.45)', fontSize:'.77rem', lineHeight:1.7 }}>
                    Authenticator uygulamasındaki<br/>6 haneli kodu girin
                  </p>
                </div>
                {err && <div style={{ marginBottom:18, padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.2)', color:'#fca5a5', fontSize:'.78rem', textAlign:'center' }}>{err}</div>}
                <OtpInput value={otp} onChange={setOtp} onComplete={verifyOtp} />
                <button onClick={()=>verifyOtp()} style={{ width:'100%', padding:'13px', borderRadius:14, border:'none', marginTop:22, cursor:(otp.length<6||busy)?'not-allowed':'pointer', background:otp.replace(/\s/g,'').length===6?'linear-gradient(135deg,#f27127,#c44b0a)':'rgba(255,255,255,.05)', color:otp.replace(/\s/g,'').length===6?'#fff':'rgba(255,255,255,.25)', fontSize:'.88rem', fontWeight:700, fontFamily:"'Outfit',sans-serif", boxShadow:otp.replace(/\s/g,'').length===6?'0 4px 24px rgba(242,113,39,.3)':'none', opacity:busy?.7:1, transition:'all .25s', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {busy?<div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite' }} />:'Etkinleştir'}
                </button>
                <button onClick={()=>setStep(1)} style={{ width:'100%', marginTop:10, padding:'10px', borderRadius:13, border:'1px solid rgba(255,255,255,.05)', background:'transparent', color:'rgba(240,238,255,.3)', fontSize:'.78rem', cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>
                  ← Geri Dön
                </button>
              </div>
            )}

            {/* STEP 3 — Done */}
            {step === 3 && (
              <div className="done-enter" style={{ textAlign:'center', padding:'12px 0' }}>
                <div style={{ width:64, height:64, borderRadius:20, margin:'0 auto 20px', background:'rgba(16,185,129,.1)', border:'1px solid rgba(16,185,129,.3)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 40px rgba(16,185,129,.2)' }}>
                  <FaCheck style={{ color:'#10b981', fontSize:'1.5rem' }} />
                </div>
                <h2 style={{ color:'#f0eeff', fontWeight:800, fontSize:'1.1rem', marginBottom:10 }}>2FA Aktifleştirildi!</h2>
                <p style={{ color:'rgba(240,238,255,.4)', fontSize:'.78rem', lineHeight:1.7, marginBottom:28 }}>
                  Hesabınız artık iki faktörlü kimlik doğrulama ile<br/>korunmaktadır. Her girişte kod istenecek.
                </p>
                <button onClick={()=>{ const u=JSON.parse(localStorage.getItem('adminUser')||'{}'); u.twoFactorEnabled=true; localStorage.setItem('adminUser',JSON.stringify(u)); window.location.replace('/dashboard'); }} style={{ width:'100%', padding:'13px', borderRadius:14, border:'none', cursor:'pointer', background:'linear-gradient(135deg,#10b981,#059669)', color:'#fff', fontSize:'.88rem', fontWeight:700, fontFamily:"'Outfit',sans-serif", boxShadow:'0 4px 24px rgba(16,185,129,.25)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  <span>Panele Giriş Yap</span><FaChevronRight style={{ fontSize:'.65rem' }} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
